import { z } from 'zod';

export const ADMIN_JOB_IDENTIFIER_MAX_LENGTH = 160;
export const ADMIN_JOB_DISPLAY_TEXT_MAX_LENGTH = 256;
export const ADMIN_JOB_ATTEMPTS_MAX = 1_000_000;
export const ADMIN_JOB_LOG_MAX_ENTRIES = 100;
export const ADMIN_JOB_COUNTER_MAX = 2_000_000_000;
export const ADMIN_JOB_CURSOR_MAX_LENGTH = 512;
export const ADMIN_JOB_SNAPSHOT_MAX_REFERENCES = 5_000;
export const ADMIN_JOB_TIMESTAMP_MAX = 8_640_000_000_000_000;

const AdminJobIdentifierSchema = z.string()
  .min(1)
  .max(ADMIN_JOB_IDENTIFIER_MAX_LENGTH)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/);

const AdminJobDisplayTextSchema = z.string()
  .min(1)
  .max(ADMIN_JOB_DISPLAY_TEXT_MAX_LENGTH);

const AdminJobTimestampSchema = z.number()
  .int()
  .nonnegative()
  .max(ADMIN_JOB_TIMESTAMP_MAX);

const AdminJobIsoTimestampSchema = z.string().datetime({ offset: true });

/**
 * Public queue-name SSOT for the worker runtime and operational admin API.
 * Keeping this tuple shared prevents a queue becoming runnable but invisible
 * to staff, or visible to staff without a corresponding worker consumer.
 */
export const ADMIN_JOB_QUEUE_NAMES = ['processing', 'case-wiki'] as const;

/** The two BullMQ queues deliberately exposed by the operational admin API. */
export const AdminJobQueueSchema = z.enum(ADMIN_JOB_QUEUE_NAMES);

/**
 * Bounded allowlist for a BullMQ job. Payloads, results, failure details, and
 * log text are deliberately absent because they can contain legal material.
 */
export const AdminJobSummarySchema = z.object({
  id: AdminJobIdentifierSchema,
  queue: AdminJobQueueSchema,
  type: AdminJobIdentifierSchema,
  user_id: AdminJobIdentifierSchema.nullable(),
  cabinet_id: AdminJobIdentifierSchema.nullable(),
  case_id: AdminJobIdentifierSchema.nullable(),
  request_id: AdminJobIdentifierSchema.nullable(),
  stage: AdminJobIdentifierSchema.nullable(),
  progress: z.number().int().min(0).max(100).nullable(),
  workflow_type: AdminJobIdentifierSchema.nullable(),
  cabinet_name: AdminJobDisplayTextSchema.nullable(),
  status: AdminJobIdentifierSchema,
  created_at: AdminJobIsoTimestampSchema,
  duration_ms: AdminJobTimestampSchema.nullable(),
  attempts: z.number().int().min(0).max(ADMIN_JOB_ATTEMPTS_MAX),
  error: z.literal('Job failed').nullable(),
}).strict();

/** Detail resource returned by `GET /api/admin/jobs/:id?queue=...`. */
export const AdminJobDetailResponseSchema = AdminJobSummarySchema.extend({
  logs: z.array(z.string()),
}).strict();

/** Cursor metadata returned with the bounded admin jobs list. */
export const AdminJobsPaginationSchema = z.object({
  limit: z.number().int().min(1).max(ADMIN_JOB_LOG_MAX_ENTRIES),
  returned: z.number().int().min(0).max(ADMIN_JOB_LOG_MAX_ENTRIES),
  hasMore: z.boolean(),
  nextCursor: z.string()
    .min(1)
    .max(ADMIN_JOB_CURSOR_MAX_LENGTH)
    .regex(/^[A-Za-z0-9_-]+$/)
    .nullable(),
  snapshotAt: AdminJobIsoTimestampSchema,
  expiresAt: AdminJobIsoTimestampSchema,
  snapshotTruncated: z.boolean(),
  snapshotSize: z.number().int().min(0).max(ADMIN_JOB_SNAPSHOT_MAX_REFERENCES),
}).strict().superRefine((pagination, context) => {
  if (pagination.returned > pagination.limit) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['returned'],
      message: 'admin jobs pagination returned cannot exceed limit',
    });
  }
  if (pagination.hasMore !== (pagination.nextCursor !== null)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['nextCursor'],
      message: 'admin jobs pagination nextCursor must be present exactly when hasMore is true',
    });
  }
});

/** List resource returned by `GET /api/admin/jobs/queue/:name`. */
export const AdminJobsListResponseSchema = z.object({
  data: z.array(AdminJobSummarySchema).max(ADMIN_JOB_LOG_MAX_ENTRIES),
  pagination: AdminJobsPaginationSchema,
}).strict().superRefine((response, context) => {
  if (response.data.length !== response.pagination.returned) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['pagination', 'returned'],
      message: 'admin jobs pagination returned must equal the number of serialized jobs',
    });
  }
});

export type AdminJobQueue = z.infer<typeof AdminJobQueueSchema>;
export type AdminJobSummary = z.infer<typeof AdminJobSummarySchema>;
export type AdminJobDetailResponse = z.infer<typeof AdminJobDetailResponseSchema>;
export type AdminJobsPagination = z.infer<typeof AdminJobsPaginationSchema>;
export type AdminJobsListResponse = z.infer<typeof AdminJobsListResponseSchema>;
